/**
 * Settings Page
 * Author settings for profile, subdomain, and custom domain management
 */
import { useState, useEffect, type FormEvent, type ChangeEvent } from "react";
import type { Author } from "@novelreader/types";
import { api, ApiError } from "../lib/api";
import { updateUser } from "../lib/auth";
import { DomainSetup } from "../components/DomainSetup";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ProfileFormData {
  name: string;
}

interface SubdomainFormData {
  subdomain: string;
}

// ---------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------

/**
 * Validate subdomain format
 * - Lowercase alphanumeric characters and hyphens only
 * - Must start and end with alphanumeric
 * - 1-63 characters
 */
function isValidSubdomain(value: string): boolean {
  if (!value) return false;
  // Single character: must be alphanumeric
  if (value.length === 1) {
    return /^[a-z0-9]$/.test(value);
  }
  // Multiple characters: must start/end with alphanumeric, can contain hyphens
  return /^[a-z0-9][a-z0-9-]{0,61}[a-z0-9]$/.test(value);
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function Settings() {
  // Author data state
  const [author, setAuthor] = useState<Author | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Profile form state
  const [profileForm, setProfileForm] = useState<ProfileFormData>({
    name: "",
  });
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileSuccess, setProfileSuccess] = useState(false);
  const [profileError, setProfileError] = useState<string | null>(null);

  // Subdomain form state
  const [subdomainForm, setSubdomainForm] = useState<SubdomainFormData>({
    subdomain: "",
  });
  const [subdomainSaving, setSubdomainSaving] = useState(false);
  const [subdomainSuccess, setSubdomainSuccess] = useState(false);
  const [subdomainError, setSubdomainError] = useState<string | null>(null);

  // Domain state
  const [domainSaving, setDomainSaving] = useState(false);
  const [domainSuccess, setDomainSuccess] = useState(false);
  const [domainError, setDomainError] = useState<string | null>(null);

  // Fetch author data
  async function fetchAuthor() {
    setIsLoading(true);
    setError(null);
    try {
      const { author: data } = await api.authors.getCurrentAuthor();
      setAuthor(data);
      // Initialize form states (name comes from user table via author response)
      setProfileForm({
        name: data.name ?? "",
      });
      setSubdomainForm({
        subdomain: data.subdomain ?? "",
      });
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError("Failed to load settings");
      }
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    fetchAuthor();
  }, []);

  // Clear success messages after timeout
  useEffect(() => {
    if (profileSuccess) {
      const timer = setTimeout(() => setProfileSuccess(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [profileSuccess]);

  useEffect(() => {
    if (subdomainSuccess) {
      const timer = setTimeout(() => setSubdomainSuccess(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [subdomainSuccess]);

  useEffect(() => {
    if (domainSuccess) {
      const timer = setTimeout(() => setDomainSuccess(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [domainSuccess]);

  // Profile handlers
  function handleProfileChange(e: ChangeEvent<HTMLInputElement>) {
    const { name, value } = e.target;
    setProfileForm((prev) => ({ ...prev, [name]: value }));
    setProfileError(null);
  }

  async function handleProfileSubmit(e: FormEvent) {
    e.preventDefault();
    setProfileSaving(true);
    setProfileError(null);
    setProfileSuccess(false);

    try {
      // Update user name via better-auth
      await updateUser({ name: profileForm.name || undefined });
      // Update local state
      if (author) {
        setAuthor({ ...author, name: profileForm.name });
      }
      setProfileSuccess(true);
    } catch (err) {
      if (err instanceof Error) {
        setProfileError(err.message);
      } else {
        setProfileError("Failed to save profile");
      }
    } finally {
      setProfileSaving(false);
    }
  }

  // Subdomain handlers
  function handleSubdomainChange(e: ChangeEvent<HTMLInputElement>) {
    const value = e.target.value.toLowerCase();
    setSubdomainForm({ subdomain: value });
    setSubdomainError(null);
  }

  async function handleSubdomainSubmit(e: FormEvent) {
    e.preventDefault();

    // Validate
    if (!isValidSubdomain(subdomainForm.subdomain)) {
      setSubdomainError(
        "Subdomain must contain only lowercase letters, numbers, and hyphens",
      );
      return;
    }

    setSubdomainSaving(true);
    setSubdomainError(null);
    setSubdomainSuccess(false);

    try {
      const { author: updated } = await api.authors.updateAuthor({
        subdomain: subdomainForm.subdomain,
      });
      setAuthor(updated);
      setSubdomainSuccess(true);
    } catch (err) {
      if (err instanceof ApiError) {
        setSubdomainError(err.message);
      } else {
        setSubdomainError("Failed to save subdomain");
      }
    } finally {
      setSubdomainSaving(false);
    }
  }

  // Custom domain handlers
  async function handleDomainSave(domain: string) {
    setDomainSaving(true);
    setDomainError(null);
    setDomainSuccess(false);

    try {
      const { author: updated } = await api.authors.updateAuthor({
        customDomain: domain,
      });
      setAuthor(updated);
      setDomainSuccess(true);
    } catch (err) {
      if (err instanceof ApiError) {
        setDomainError(err.message);
      } else {
        setDomainError("Failed to save custom domain");
      }
    } finally {
      setDomainSaving(false);
    }
  }

  async function handleDomainRemove() {
    setDomainSaving(true);
    setDomainError(null);
    setDomainSuccess(false);

    try {
      const { author: updated } = await api.authors.updateAuthor({
        customDomain: null,
      });
      setAuthor(updated);
      setDomainSuccess(true);
    } catch (err) {
      if (err instanceof ApiError) {
        setDomainError(err.message);
      } else {
        setDomainError("Failed to remove custom domain");
      }
    } finally {
      setDomainSaving(false);
    }
  }

  // Loading state
  if (isLoading) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="animate-pulse space-y-6">
          <div className="h-8 w-32 bg-slate-200 rounded" />
          <div className="h-4 w-64 bg-slate-200 rounded" />
          <div className="bg-white rounded-lg border border-slate-200 p-6 space-y-4">
            <div className="h-6 w-24 bg-slate-200 rounded" />
            <div className="h-10 bg-slate-200 rounded" />
            <div className="h-10 bg-slate-200 rounded" />
          </div>
        </div>
        <p className="sr-only">Loading settings...</p>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="max-w-4xl mx-auto">
        <h1 className="text-2xl font-bold text-slate-900 mb-6">Settings</h1>
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <p className="text-red-800 mb-4">{error}</p>
          <button
            onClick={fetchAuthor}
            className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // Main content
  return (
    <div className="max-w-4xl mx-auto space-y-8">
      {/* Page Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Settings</h1>
        <p className="mt-1 text-slate-600">
          Manage your account settings and preferences.
        </p>
      </div>

      {/* Profile Section */}
      <section className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
        <h2 className="text-lg font-semibold text-slate-900 mb-4">Profile</h2>

        <form onSubmit={handleProfileSubmit} className="space-y-4">
          {/* Name */}
          <div>
            <label
              htmlFor="name"
              className="block text-sm font-medium text-slate-700 mb-1"
            >
              Name
            </label>
            <input
              id="name"
              name="name"
              type="text"
              value={profileForm.name}
              onChange={handleProfileChange}
              className="w-full px-3 py-2 border border-slate-300 rounded-md bg-white text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              placeholder="Your name"
            />
          </div>

          {/* Read-only fields */}
          <div className="pt-2 border-t border-slate-200">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <span className="block text-sm font-medium text-slate-700">
                  Username
                </span>
                <span className="text-sm text-slate-600">
                  {author?.username}
                </span>
              </div>
              <div>
                <span className="block text-sm font-medium text-slate-700">
                  Email
                </span>
                <span className="text-sm text-slate-600">{author?.email}</span>
              </div>
            </div>
          </div>

          {/* Error/Success Messages */}
          {profileError && (
            <p className="text-sm text-red-600">{profileError}</p>
          )}
          {profileSuccess && (
            <p className="text-sm text-green-600">Profile saved successfully</p>
          )}

          {/* Submit Button */}
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={profileSaving}
              className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {profileSaving ? "Saving..." : "Save Profile"}
            </button>
          </div>
        </form>
      </section>

      {/* Subdomain Section */}
      <section className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
        <h2 className="text-lg font-semibold text-slate-900 mb-4">Subdomain</h2>

        <form onSubmit={handleSubdomainSubmit} className="space-y-4">
          <div>
            <label
              htmlFor="subdomain"
              className="block text-sm font-medium text-slate-700 mb-1"
            >
              Subdomain
            </label>
            <div className="flex items-center gap-2">
              <input
                id="subdomain"
                type="text"
                value={subdomainForm.subdomain}
                onChange={handleSubdomainChange}
                className="flex-1 px-3 py-2 border border-slate-300 rounded-md bg-white text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                placeholder="yourname"
              />
              <span className="text-slate-500">.reader.nrplatform.com</span>
            </div>
            {/* Preview URL */}
            {subdomainForm.subdomain && (
              <p className="mt-2 text-sm text-slate-600">
                Your site will be available at:{" "}
                <span className="font-medium text-slate-900">
                  {subdomainForm.subdomain}.reader.nrplatform.com
                </span>
              </p>
            )}
          </div>

          {/* Error/Success Messages */}
          {subdomainError && (
            <p className="text-sm text-red-600">{subdomainError}</p>
          )}
          {subdomainSuccess && (
            <p className="text-sm text-green-600">
              Subdomain saved successfully
            </p>
          )}

          {/* Submit Button */}
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={subdomainSaving}
              className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {subdomainSaving ? "Saving..." : "Save Subdomain"}
            </button>
          </div>
        </form>
      </section>

      {/* Custom Domain Section */}
      <section className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
        <h2 className="text-lg font-semibold text-slate-900 mb-4">
          Custom Domain
        </h2>

        {/* Error/Success Messages */}
        {domainError && (
          <p className="text-sm text-red-600 mb-4">{domainError}</p>
        )}
        {domainSuccess && (
          <p className="text-sm text-green-600 mb-4">
            Domain settings saved successfully
          </p>
        )}

        <DomainSetup
          customDomain={author?.customDomain}
          customDomainStatus={author?.customDomainStatus}
          onSave={handleDomainSave}
          onRemove={handleDomainRemove}
          isLoading={domainSaving}
        />
      </section>
    </div>
  );
}
